import { paymentModeEnum } from "../../../../state/initialState";
import {
  removeGlobalErrorMessageIfMatchesThisString,
  updateGlobalErrorMessage,
} from "../../../../actions/globalError";
import React, { Component } from "react";
import { connect } from "react-redux";
import objectAssign from "object-assign";
import SquarePaymentForm, {
  CreditCardNumberInput,
  CreditCardPostalCodeInput,
  CreditCardExpirationDateInput,
  CreditCardCVVInput,
  CreditCardSubmitButton,
} from "react-square-payment-form";
import {
  updateCheckoutAppointmentStepData,
  updateGettingStripeToken,
  updateBookingToInvoice,
  changeTipPopupVisibility,
} from "../../../../actions/checkoutAppointmentStep";
import { decimalPoint } from "../../../../utils/helpers";
import { ValidMe } from "../../../shared/validMeReact";
import Txt from "../../../shared/textInput";
import { updateGlobalSuccessMessage } from "../../../../actions/globalSuccess";

/**
 * Due to previous payment flow (stripe), a few minor hacks were required. See creditCardPaymentForm for original implementation
 * With Square, form validation only occurs after submit. In componentDidMount we set all the validations to true
 * as this is the only way the submit button (Pay) becomes available. We then check and set errors in local state.
 *
 * this.state.saveCard. In some workflows (modal, save buttton) we are saving the card, instead of paying.
 * Saving a card still attaches it to an appointment. Tried to minimize changes to existing flow as a lot of side effects.
 */
@connect(mapStateToProps)
class SquareForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      errorMessages: [],
      saveCard: this.props.modalMode, // Modal only used for saving
    };
  }

  dispatchUpdateCheckoutAppointmentStepData = (data) => {
    this.props.dispatch(updateCheckoutAppointmentStepData(data));
  };

  updateCheckoutStep = (data) => {
    let newData = objectAssign(
      {},
      this.props.checkoutAppointmentStepData,
      data
    );
    this.dispatchUpdateCheckoutAppointmentStepData(newData);
  };

  componentDidMount() {
    this.updateCheckoutStep({
      cardCvvValid: true,
      cardNumberValid: true,
      cardExpValid: true,
    });
  }

  componentDidUpdate() {
    let { doCreditCardFormPayment } = this.props.checkoutAppointmentStepData;
    if (doCreditCardFormPayment) {
      this.updateCheckoutStep({ doCreditCardFormPayment: false });
      document.getElementsByClassName("sq-creditcard")[0].click();
    }
  }

  handleNameOnCardChange = (ev) => {
    this.updateCheckoutStep({ nameOnCard: ev.target.value });
  };

  getErrMessage = (type) => {
    const { errorMessages } = this.state;
    const errMessage = errorMessages.find(
      (err) => err.field === type && err.message
    );
    return <span className="sq-error">{errMessage && errMessage.message}</span>;
  };

  canCheckoutCurrentBooking = () => {
    let { limitedPermissions, currentBookingId } = this.props;
    return (
      limitedPermissions.canCheckoutAllBookings ||
      limitedPermissions.bookingsCheckoutableByCurrentUser.indexOf(
        currentBookingId
      ) > -1
    );
  };

  changePaymentAmountWithinConstraints = (ev) => {
    let { InvoiceCantBeOverpaid } = window.APP.LANGUAGE.Errors;
    const { outstandingamount } = this.props.invoiceData;
    let paymentAmount = ev.target.value.replace("$", "");

    if (
      (/[0-9]+\.?([0-9]{0,2})?$/.test(paymentAmount) &&
        Number(paymentAmount) <= outstandingamount) ||
      paymentAmount.length == 0
    ) {
      const decimalPlace = paymentAmount.split(".")[1];
      if (decimalPlace && decimalPlace.length > 2) {
        return;
      }
      this.updateCheckoutStep({ paymentAmount: paymentAmount });
      this.props.dispatch(
        removeGlobalErrorMessageIfMatchesThisString(InvoiceCantBeOverpaid)
      );
    }
  };

  handleChangeTipPopupVisibility = (visibility) => {
    this.props.dispatch(changeTipPopupVisibility(visibility));
  };

  formatPaymentField = () => {
    let { paymentAmount } = this.props.checkoutAppointmentStepData;
    this.updateCheckoutStep({ paymentAmount: decimalPoint(paymentAmount) });
  };

  cardNonceResponseReceived = (
    errors,
    nonce,
    cardData,
    buyerVerificationToken
  ) => {
    const { saveCard } = this.state;

    this.props.dispatch(removeGlobalErrorMessageIfMatchesThisString(""));

    // These are just field errors that square returns
    if (errors) {
      this.setState({
        errorMessages: errors,
      });
      return;
    }

    this.setState({ errorMessages: [] });

    const { currentBookingId } = this.props;
    const {
      nameOnCard,
      paymentAmount,
    } = this.props.checkoutAppointmentStepData;
    const { UnknownApiError } = window.APP.LANGUAGE.Errors;
    let {
      PaymentSuccessfulMessage,
      StoreCardSuccessfully,
    } = window.APP.LANGUAGE.Success;

    this.props.dispatch(removeGlobalErrorMessageIfMatchesThisString(""));

    let cb = () => {
      const { afterSaveHandler } = this.props;

      this.props.dispatch(
        updateGlobalSuccessMessage({
          message: saveCard ? StoreCardSuccessfully : PaymentSuccessfulMessage,
          icon: "saved_icon",
        })
      );

      if (afterSaveHandler) {
        this.props.afterSaveHandler();
      }
    };
    const chargeAmount = Number(paymentAmount);

    const payload = {
      token: nonce,
    };

    // If no amount is sent, will only save card, not charge
    if (!saveCard) {
      payload.chargeamount = chargeAmount;
    }

    this.props.dispatch(updateBookingToInvoice(currentBookingId, payload, cb));
  };

  storeCardForLaterUse = () => {
    this.setState({
      saveCard: true,
    });

    const storeCardAgainstBooking = () => {
      document.getElementsByClassName("sq-creditcard")[0].click();
    };

    if (this.props.beforeSaveHandler) {
      this.props.beforeSaveHandler(storeCardAgainstBooking);
    } else {
      storeCardAgainstBooking();
    }
  };

  getCreditCardPaymentCard = () => {
    const { bookingPaymentsData } = this.props;
    if (bookingPaymentsData && bookingPaymentsData.payments) {
      const offlineCardPayment = bookingPaymentsData.payments.find(
        (payment) => {
          return payment.card.cardpresent === false;
        }
      );
      if (offlineCardPayment) {
        return offlineCardPayment.card;
      }
    }
  };

  getCardPaymentForm = () => {
    const { paymentMode, invoiceData, posSettings } = this.props;
    const {
      nameOnCard,
      paymentAmount,
    } = this.props.checkoutAppointmentStepData;
    let { PaymentAmountMissing } = window.APP.LANGUAGE.Errors;
    let {
      NameOnCardPlaceholder,

      IncludedTipButton,
      PaymentAmountPlaceholder,
      PaymentSubTitle,
    } = window.APP.LANGUAGE.Pages.AppointmentCheckout;
    let disabled = !this.canCheckoutCurrentBooking();

    const disablePayment = invoiceData.status == "PAID";

    let cardForm = () => {
      const cardObject = this.getCreditCardPaymentCard();
      if (cardObject) {
        const cardDataLast4 = cardObject.last4;
        const cardDataType = cardObject.type;
        return (
          <div className="card_icon">
            <div className="card_icon_img" />
            <p>
              {cardDataType} x{cardDataLast4}
            </p>
          </div>
        );
      }

      //TODO CONTINUE HERE
      let that = this;
      return (
        <React.Fragment>
          <Txt
            maxLength="50"
            className="card_name square"
            disabled={disablePayment}
            type="input"
            fieldclasses="mrnull"
            onChange={this.handleNameOnCardChange}
            placeholder={NameOnCardPlaceholder}
            value={nameOnCard}
          ></Txt>
          <SquarePaymentForm
            sandbox={window.APP.squareKey.includes("sandbox")}
            applicationId={window.APP.squareKey}
            cardNonceResponseReceived={this.cardNonceResponseReceived}
            inputStyles={[
              {
                fontSize: "13px",
                lineHeight: "28px",
                padding: "0px",
              },
            ]}
          >
            <div className="sq-field">
              <CreditCardNumberInput label="Card Number" />
              {this.getErrMessage("cardNumber")}
            </div>
            <div className={this.state.saveCard ? "sq-save" : "sq-line-wrap"}>
              <div className="sq-field">
                <CreditCardExpirationDateInput label="Expiry" />
                {this.getErrMessage("expirationDate")}
              </div>
              <div className="sq-field sq-field--2">
                <CreditCardCVVInput />
                {this.getErrMessage("cvv")}
              </div>
            </div>
            <div className="sq-field sq-field--3">
              <CreditCardPostalCodeInput />
              {this.getErrMessage("postalCode")}
            </div>

            <CreditCardSubmitButton>Square Submit</CreditCardSubmitButton>
          </SquarePaymentForm>
        </React.Fragment>
      );
    };

    let tipLink = (
      <a
        className="tip_link"
        onClick={this.handleChangeTipPopupVisibility.bind(this, true)}
      >
        {IncludedTipButton}
      </a>
    );
    if (disabled || disablePayment) {
      tipLink = <a className="tip_link disabled">{IncludedTipButton}</a>;
    }

    return (
      <div className="ui form credit_card_form">
        {cardForm()}

        {this.props.modalMode ? (
          ""
        ) : (
          <div>
            <div className="tipLabel">
              <label className="tipLabel_title">{PaymentSubTitle}</label>
              &nbsp;
              {posSettings &&
              posSettings.tipssettings &&
              posSettings.tipssettings.enabled
                ? tipLink
                : ""}
            </div>
            <div className="totalLabel">
              <ValidMe
                validmemessage={PaymentAmountMissing}
                fieldclasses="mrnull"
                validmefor="required"
                group="thirdStepCheckoutAppointment"
              >
                <Txt
                  type="input"
                  onBlur={this.formatPaymentField}
                  fieldclasses="mrnull"
                  className="payment_amount active"
                  disabled={disablePayment}
                  onChange={this.changePaymentAmountWithinConstraints}
                  placeholder={PaymentAmountPlaceholder}
                  value={paymentAmount > 0 ? "$" + paymentAmount : ""}
                ></Txt>
              </ValidMe>
            </div>
          </div>
        )}
      </div>
    );
  };

  canCheckoutCurrentBooking = () => {
    let { limitedPermissions, currentBookingId } = this.props;
    return (
      limitedPermissions.canCheckoutAllBookings ||
      limitedPermissions.bookingsCheckoutableByCurrentUser.indexOf(
        currentBookingId
      ) > -1
    );
  };

  getStoreCard = () => {
    const { paymentMode } = this.props;
    const {
      nameOnCard,
      cardNumberValid,
      cardExpValid,
      cardCvvValid,
    } = this.props.checkoutAppointmentStepData;
    let {
      StoreCardForLaterTitle,
      SaveCardButton,
    } = window.APP.LANGUAGE.Pages.AppointmentCheckout;

    if (paymentMode != paymentModeEnum.CARD_MODE) return "";

    let disabled = !this.canCheckoutCurrentBooking();

    const cardObject = this.getCreditCardPaymentCard();

    let completelyDisabled =
      disabled ||
      !nameOnCard ||
      !cardNumberValid ||
      !cardExpValid ||
      !cardCvvValid;

    if (cardObject) {
      return <div />;
    } else {
      if (this.props.modalMode) {
        if (completelyDisabled) {
          return (
            <div className="ui button disabled save-card">{SaveCardButton}</div>
          );
        } else {
          return (
            <div
              className="ui button save-card"
              onClick={this.storeCardForLaterUse}
            >
              {SaveCardButton}
            </div>
          );
        }
      } else if (completelyDisabled) {
        return (
          <div className="credit_card_store _disabled">
            {StoreCardForLaterTitle}
          </div>
        );
      } else {
        return (
          <div
            className="credit_card_store _active"
            onClick={this.storeCardForLaterUse}
          >
            {StoreCardForLaterTitle}
          </div>
        );
      }
    }
  };

  render() {
    return (
      <div className={`${this.props.modalMode ? "payment_box" : ""}`}>
        {this.getCardPaymentForm()}
        <div>{this.getStoreCard()}</div>
      </div>
    );
  }
}

export default SquareForm;

function mapStateToProps(state) {
  return {
    currentBookingId: state.bookingReducer.currentBookingId,
    paymentMode: state.appointmentCheckoutReducer.paymentMode,
    checkoutMode: state.appointmentCheckoutReducer.checkoutMode,
    limitedPermissions: state.appointmentStepReducer.limitedPermissions,
    posSettings: state.appointmentCheckoutReducer.posSettings,
    invoiceData: state.appointmentCheckoutReducer.invoiceData,
    appointmentStepData: state.appointmentStepReducer.appointmentStepData,
    bookingPaymentsData: state.appointmentCheckoutReducer.bookingPaymentsData,
    checkoutAppointmentStepData:
      state.appointmentCheckoutReducer.checkoutAppointmentStepData,
    checkoutPreviousMenu: state.menuReducer.checkoutPreviousMenu,
  };
}
